"use client"

import { useState, useEffect, useMemo } from "react"
import { ref, onValue, remove, set } from "../lib/firebase-database"
import { db } from "../firebase"
import { Layout } from "./Layout"
import { Loader2, Shuffle, Search, Crown } from "lucide-react"
import { Switch } from "./ui/switch"
import { useAuth } from "../hooks/useAuth"
import { useNavigate } from "react-router-dom"

export default function LuxuryVillas() {
  const { user, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [luxuryVillas, setLuxuryVillas] = useState([])
  const [availableVillas, setAvailableVillas] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [isShuffling, setIsShuffling] = useState(false)
  const [lastShuffleDate, setLastShuffleDate] = useState(null)

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate("/")
      return
    }
    setIsLoading(true)
    setError(null)
    const luxuryRef = ref(db, "luxuryVillas")
    const villasRef = ref(db, "villas")
    const shuffleRef = ref(db, "luxuryShuffleData")

    onValue(shuffleRef, (snap) => {
      const d = snap.val()
      if (d) setLastShuffleDate(d.lastShuffleDate)
    })

    onValue(
      luxuryRef,
      (snapshot) => {
        const data = snapshot.val()
        setLuxuryVillas(
          data
            ? Object.entries(data).map(([id, value]) => ({ id, ...value }))
            : []
        )
      },
      (err) => setError(`Failed to load luxury villas: ${err.message}`)
    )

    onValue(
      villasRef,
      (snapshot) => {
        const data = snapshot.val()
        setAvailableVillas(
          data
            ? Object.entries(data).map(([id, villa]) => ({ id, ...villa }))
            : []
        )
        setIsLoading(false)
      },
      (err) => {
        setError(`Failed to load villas: ${err.message}`)
        setIsLoading(false)
      }
    )
  }, [user, isAdmin, navigate])

  const handleToggleLuxury = async (villa, makeLuxury) => {
    // Optimistic UI update (so the toggle reflects immediately without polling)
    setLuxuryVillas((prev) => {
      if (makeLuxury) {
        if (prev.some((lv) => lv.villaId === villa.id || lv.id === villa.id)) {
          return prev
        }
        return [
          ...prev,
          { id: villa.id, villaId: villa.id, addedAt: new Date().toISOString() },
        ]
      }
      return prev.filter((lv) => lv.villaId !== villa.id && lv.id !== villa.id)
    })

    try {
      if (makeLuxury) {
        // luxury_villas D1 columns: villaId, order_pos, addedAt
        await set(ref(db, `luxuryVillas/${villa.id}`), {
          villaId: villa.id,
          addedAt: new Date().toISOString(),
        })
      } else {
        await remove(ref(db, `luxuryVillas/${villa.id}`))
      }
    } catch (err) {
      // Roll back on failure
      setLuxuryVillas((prev) => {
        if (makeLuxury) {
          return prev.filter(
            (lv) => lv.villaId !== villa.id && lv.id !== villa.id
          )
        }
        return [
          ...prev,
          { id: villa.id, villaId: villa.id, addedAt: new Date().toISOString() },
        ]
      })
      setError(`Failed to update luxury status: ${err.message}`)
    }
  }

  const handleManualShuffle = async () => {
    if (luxuryVillas.length === 0) {
      setError("No luxury villas to shuffle")
      return
    }
    setIsShuffling(true)
    try {
      const shuffledIds = [...luxuryVillas.map((v) => v.id)]
      for (let i = shuffledIds.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[shuffledIds[i], shuffledIds[j]] = [shuffledIds[j], shuffledIds[i]]
      }
      const shuffleData = {
        shuffledOrder: shuffledIds,
        lastShuffleDate: new Date().toISOString(),
        isAutomaticShuffle: false,
      }
      await set(ref(db, "luxuryShuffleData"), shuffleData)
      setLastShuffleDate(shuffleData.lastShuffleDate)
    } catch (err) {
      setError(`Failed to shuffle luxury villas: ${err.message}`)
    } finally {
      setIsShuffling(false)
    }
  }

  const isLuxuryVilla = (villaId) =>
    luxuryVillas.some(
      (lv) => lv.villaId === villaId || lv.id === villaId
    )

  const filteredVillas = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return availableVillas
    return availableVillas.filter(
      (v) =>
        v.name?.toLowerCase().includes(q) ||
        v.owner?.name?.toLowerCase().includes(q)
    )
  }, [availableVillas, searchQuery])

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Luxury Villas</h1>
          <p className="text-sm text-gray-500 mt-1">
            Mark villas as luxury. They auto-shuffle daily on the homepage.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-4 py-3">
            {error}
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-gray-700" />
                <h3 className="text-sm font-semibold text-gray-900">
                  Shuffle settings
                </h3>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Last shuffle:{" "}
                {lastShuffleDate
                  ? new Date(lastShuffleDate).toLocaleString()
                  : "Never"}
                {" · "}
                {luxuryVillas.length} luxury{" "}
                {luxuryVillas.length === 1 ? "villa" : "villas"}
              </p>
            </div>
            <button
              type="button"
              onClick={handleManualShuffle}
              disabled={isShuffling || luxuryVillas.length === 0}
              className="inline-flex items-center justify-center px-3 py-2 bg-black text-white text-sm font-medium rounded-md hover:bg-gray-900 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isShuffling ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Shuffle className="h-4 w-4 mr-2" />
              )}
              {isShuffling ? "Shuffling..." : "Manual Shuffle"}
            </button>
          </div>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search villas by name or owner"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black"
          />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading villas...
          </div>
        ) : filteredVillas.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg py-16 text-center text-sm text-gray-500">
            No villas to show.
          </div>
        ) : (
          <div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
            {filteredVillas.map((villa) => (
              <div
                key={villa.id}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden flex"
              >
                <div className="w-32 sm:w-40 h-32 sm:h-36 shrink-0 bg-gray-100">
                  <img
                    src={villa.images?.[0] || villa.thumbnail || "/placeholder.svg"}
                    alt={villa.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 p-4 flex flex-col gap-1.5 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="text-sm font-semibold text-gray-900 line-clamp-1">
                      {villa.name}
                    </h4>
                    <span className="text-sm font-semibold text-gray-900 shrink-0">
                      ₹{villa.rate}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-1">
                    {villa.owner?.name || "—"}
                  </p>
                  <p className="text-xs text-gray-500">
                    Rating: {villa.averageRating?.toFixed?.(1) || "N/A"}
                  </p>
                  <div className="mt-auto pt-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-700">
                      Luxury
                    </span>
                    <Switch
                      checked={isLuxuryVilla(villa.id)}
                      onCheckedChange={(checked) =>
                        handleToggleLuxury(villa, checked)
                      }
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  )
}
